import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save, Trash2, Loader2 } from 'lucide-react'
import { showSaveSuccess, showSaveError, showValidationError, showLoading, closeSwal, showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { creditNotesService } from '@/services/creditNotesService'
import { financeService } from '@/services/financeService'
import {
  CreditNote,
  CreditNoteLine,
  FinanceCustomer,
  TaxRate,
  CreditNoteListItem,
} from '@/api/types'

export const CreditNotesDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore()

  const isEditMode = id !== 'new' && id !== undefined
  const creditNoteFromList = (location.state as any)?.creditNote as CreditNoteListItem | undefined
  const branchId = user?.selectedBranch?.branchId
  const companyId = 0 // Placeholder until company context is available

  // Helper function to find 15% VAT tax rate (prioritize taxRateId: 1)
  const getDefaultTaxRate = (taxRates: TaxRate[]): { taxRateId: number; taxRate: number } | null => {
    // First, try to find taxRateId === 1
    const taxById = taxRates.find(t => t.taxRateId === 1)
    if (taxById) {
      return {
        taxRateId: taxById.taxRateId,
        taxRate: taxById.taxPercent || taxById.taxRate || 0
      }
    }
    // Fallback: find by 15% rate
    const tax15 = taxRates.find(t => {
      const rate = t.taxPercent || t.taxRate || 0
      return Math.abs(rate - 15) < 0.01 // Allow small floating point differences
    })
    if (tax15) {
      return {
        taxRateId: tax15.taxRateId,
        taxRate: tax15.taxPercent || tax15.taxRate || 0
      }
    }
    return null
  }

  const getDefaultRows = (taxRates: TaxRate[] = []): CreditNoteLine[] => {
    const defaultTax = getDefaultTaxRate(taxRates)
    return Array.from({ length: 3 }, (): CreditNoteLine => ({
      lineId: 0,
      invLineId: 0,
      creditNoteId: 0,
      itemId: 0,
      description: '',
      qty: 0,
      unitPrice: 0,
      itemPrice: 0,
      discountPercent: 0,
      discountAmount: 0,
      taxableAmount: 0,
      taxRateId: defaultTax?.taxRateId || 0,
      taxRate: defaultTax?.taxRate || 0,
      taxPercent: defaultTax?.taxRate || 0,
      taxAmount: 0,
      lineAmount: 0,
      companyId: companyId,
      branchId: branchId || 0,
    }))
  }

  const [creditNote, setCreditNote] = useState<CreditNote>({
    creditNoteId: 0,
    creditNoteDate: new Date().toISOString().split('T')[0],
    creditNoteNumber: '',
    customerId: 0,
    supplyDate: '',
    refInvNumber: '',
    refInvId: 0,
    currency: 0,
    conversionRate: 1,
    taxRateId: 0,
    subTotal: 0,
    totalTax: 0,
    totalDiscount: 0,
    total: 0,
    note: '',
    companyId: companyId,
    branchId: branchId || user?.selectedBranch?.branchId || 0,
    remainingAmount: 0,
    creditNoteLines: getDefaultRows([]),
  })

  const [customers, setCustomers] = useState<FinanceCustomer[]>([])
  const [selectedCustomerId, setSelectedCustomerId] = useState<number>(0)
  const [taxRates, setTaxRates] = useState<TaxRate[]>([])
  const [invoices, setInvoices] = useState<any[]>([])
  const [, setInvoiceLines] = useState<any[]>([])
  const [originalInvoiceLines, setOriginalInvoiceLines] = useState<Map<number, { qty: number; unitPrice: number }>>(new Map())
  const [fullyRefundedInvoices, setFullyRefundedInvoices] = useState<Set<number>>(new Set())
  const [, setPartiallyRefundedInvoices] = useState<Set<number>>(new Set())
  const [invoicesWithValidLines, setInvoicesWithValidLines] = useState<Set<number>>(new Set())
  const [checkingInvoiceLines, setCheckingInvoiceLines] = useState(false)

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deletedRowIndices, setDeletedRowIndices] = useState<Set<number>>(new Set())

  useEffect(() => {
    if (!branchId) {
      console.warn('BranchId is not available, cannot load data')
      // Call without await since useEffect callback is not async
      showValidationError('Please select a branch first')
      return
    }

    console.log('Loading lookups for branchId:', branchId)
    const loadLookups = async () => {
      try {
        setLoading(true)
        const [customersData, taxRatesData] = await Promise.all([
          financeService.getAllCustomers(branchId),
          financeService.getTaxRates(branchId),
        ])

        console.log('Loaded data:', {
          customers: customersData?.length || 0,
          taxRates: taxRatesData?.length || 0,
        })

        setCustomers(customersData || [])
        setTaxRates(taxRatesData || [])
        // Don't load invoices initially - will load when customer is selected
        setInvoices([])

        // Load existing credit notes to determine which invoices are fully/partially refunded
        if (!isEditMode) {
          try {
            const creditNotesResponse = await creditNotesService.getAllCreditNotes(branchId, 1, 1000)
            const creditNotes = creditNotesResponse.data || []
            
            const fullyRefunded = new Set<number>()
            const partiallyRefunded = new Set<number>()
            
            // Track invoices that have credit notes
            const invoiceCreditNoteMap = new Map<number, { isFullyRefund: boolean; remainingAmount: number }>()
            
            creditNotes.forEach((cn: any) => {
              const invId = cn.refInvId || cn.invoiceId
              if (invId) {
                const existing = invoiceCreditNoteMap.get(invId) || { isFullyRefund: false, remainingAmount: 0 }
                
                if (cn.isFullyRefund === true || cn.isFullyRefund === 'true') {
                  fullyRefunded.add(invId)
                  existing.isFullyRefund = true
                } else {
                  partiallyRefunded.add(invId)
                  existing.isFullyRefund = false
                }
                
                // Track remaining amount for the invoice
                const originalInv = invoices.find((inv: any) => (inv.invoiceId || inv.id) === invId)
                if (originalInv) {
                  existing.remainingAmount = originalInv.remainingAmount || 0
                }
                
                invoiceCreditNoteMap.set(invId, existing)
              }
            })
            
            console.log('Refunded invoices:', {
              fullyRefunded: Array.from(fullyRefunded),
              partiallyRefunded: Array.from(partiallyRefunded),
              invoiceCreditNoteMap: Array.from(invoiceCreditNoteMap.entries()),
            })
            
            setFullyRefundedInvoices(fullyRefunded)
            setPartiallyRefundedInvoices(partiallyRefunded)
          } catch (error) {
            console.error('Failed to load credit notes for filtering', error)
          }
        }
      } catch (error: any) {
        console.error('Failed to load credit note lookups', error)
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
        })
        await showError('Error', error.response?.data?.message || error.message || t('creditNotes.failedToLoadCreditNotes'))
        
        // Set empty arrays to prevent undefined errors
        setCustomers([])
        setTaxRates([])
        setInvoices([])
      } finally {
        setLoading(false)
      }
    }

    loadLookups()
  }, [branchId, isEditMode])

  // Set default VAT (15%) for rows that don't have taxRateId when taxRates are loaded
  useEffect(() => {
    if (taxRates.length === 0) return
    
    const defaultTax = getDefaultTaxRate(taxRates)
    if (!defaultTax) {
      console.warn('Default tax rate (15% or ID 1) not found in taxRates:', taxRates)
      return
    }

    console.log('Setting default VAT:', defaultTax)

    setCreditNote(prev => {
      const hasRowsWithoutVAT = prev.creditNoteLines?.some(line => !line.taxRateId || line.taxRateId === 0)
      if (!hasRowsWithoutVAT) return prev

      const updatedLines = prev.creditNoteLines?.map(line => {
        // Only set default VAT if taxRateId is 0 or not set
        if (!line.taxRateId || line.taxRateId === 0) {
          console.log('Setting default VAT for line:', line, 'to taxRateId:', defaultTax.taxRateId)
          return {
            ...line,
            taxRateId: defaultTax.taxRateId,
            taxRate: defaultTax.taxRate,
            taxPercent: defaultTax.taxRate,
          }
        }
        return line
      }) || []

      return { ...prev, creditNoteLines: updatedLines }
    })
  }, [taxRates])

  // Load credit note data in edit mode
  useEffect(() => {
    if (!isEditMode || !branchId || !id) return

    const loadCreditNote = async () => {
      try {
        setLoading(true)
        const creditNoteId = Number(id)
        
        console.log('Loading credit note data for edit mode:', { branchId, creditNoteId })
        
        // First, set data from list if available (for faster UI update)
        if (creditNoteFromList) {
          const customerId = creditNoteFromList.customerId || 0
          const customer = customers.find((c) => c.customerId === customerId)
          if (customer) {
            setSelectedCustomerId(customerId)
            // Load invoices for this customer
            if (customerId > 0 && branchId) {
              try {
                const invoicesData = await creditNotesService.getAllInvoices(branchId, customerId)
                setInvoices(invoicesData || [])
              } catch (error) {
                console.error('Failed to load invoices for customer in edit mode', error)
              }
            }
          }
          
          setCreditNote((prev) => ({
            ...prev,
            creditNoteId: creditNoteFromList.creditNoteId || creditNoteFromList.id || creditNoteId,
            creditNoteNumber: creditNoteFromList.creditNoteNumber || creditNoteFromList.creditNoteNo || '',
            creditNoteDate: creditNoteFromList.creditNoteDate || new Date().toISOString().split('T')[0],
            customerId: customerId,
            supplyDate: creditNoteFromList.supplyDate || '',
            refInvNumber: creditNoteFromList.refInvNumber || '',
            refInvId: creditNoteFromList.refInvId || 0,
            remainingAmount: creditNoteFromList.remainingAmount || 0,
            subTotal: creditNoteFromList.subTotal || 0,
            totalTax: creditNoteFromList.totalTax || 0,
            totalDiscount: creditNoteFromList.totalDiscount || 0,
            total: creditNoteFromList.total || 0,
            note: creditNoteFromList.note || '',
            branchId: branchId,
          }))
        }
        
        // Now load lines from API (this endpoint returns only lines)
        try {
          const creditNoteData = await creditNotesService.getCreditNoteById(branchId, creditNoteId)
          
          console.log('Loaded credit note lines from API:', creditNoteData.creditNoteLines?.length || 0)
          
          // Update with lines from API - filter out empty lines (only show lines with data)
          if (creditNoteData.creditNoteLines && creditNoteData.creditNoteLines.length > 0) {
            const validLines = creditNoteData.creditNoteLines.filter((line: CreditNoteLine) => {
              const qty = line.qty || 0
              if (qty <= 0) return false
              return !!(line.description?.trim() || (line.itemId && line.itemId > 0))
            })
            
            setCreditNote((prev) => ({
              ...prev,
              creditNoteLines: validLines.length > 0 ? validLines : [],
              // Update totals if available from API
              subTotal: creditNoteData.subTotal ?? prev.subTotal,
              totalTax: creditNoteData.totalTax ?? prev.totalTax,
              totalDiscount: creditNoteData.totalDiscount ?? prev.totalDiscount,
              total: creditNoteData.total ?? prev.total,
            }))
            
            console.log('Credit note lines set in state:', validLines.length, 'valid lines out of', creditNoteData.creditNoteLines.length)
          } else {
            // If no lines, use empty array (not default rows) in edit mode
            setCreditNote((prev) => ({
              ...prev,
              creditNoteLines: [],
            }))
          }
          
          // If invoice is referenced, load invoice lines for reference
          const refInvId = creditNoteFromList?.refInvId || creditNoteData.refInvId
          if (refInvId) {
            try {
              const invLinesResponse = await creditNotesService.getInvLines(refInvId, branchId)
              const allLines = invLinesResponse.invoiceLines || []
              
              // Filter out lines with qty = 0 or amount = 0
              const filteredLines = allLines.filter((line: any) => {
                const qty = line.qty || 0
                const lineAmount = line.lineAmount || 0
                return qty > 0 && lineAmount > 0
              })
              
              // Store original invoice line values for validation (keyed by lineId which becomes invLineId)
              const originalValuesMap = new Map<number, { qty: number; unitPrice: number }>()
              filteredLines.forEach((line: any) => {
                // Use lineId as key (this will match invLineId in credit note line)
                const lineId = line.lineId || line.invLineId || 0
                if (lineId > 0) {
                  originalValuesMap.set(lineId, {
                    qty: line.qty || 0,
                    unitPrice: line.unitPrice || 0
                  })
                }
              })
              setOriginalInvoiceLines(originalValuesMap)
              setInvoiceLines(filteredLines)
            } catch (error) {
              console.warn('Failed to load invoice lines for reference', error)
            }
          }
        } catch (linesError: any) {
          console.error('Failed to load credit note lines', linesError)
          // Don't show error for lines if we have list data
          if (!creditNoteFromList) {
            await showError('Error', linesError.response?.data?.message || linesError.message || t('creditNotes.failedToLoadCreditNote'))
          }
          // Set empty array in edit mode if lines failed to load (don't show default rows)
          setCreditNote((prev) => ({
            ...prev,
            creditNoteLines: [],
          }))
        }
      } catch (error: any) {
        console.error('Failed to load credit note data', error)
        await showError('Error', error.response?.data?.message || error.message || t('creditNotes.failedToLoadCreditNote'))
      } finally {
        setLoading(false)
      }
    }

    // Wait for customers to load before loading credit note
    if (customers.length > 0 || !isEditMode) {
      loadCreditNote()
    }
  }, [isEditMode, branchId, id, customers, creditNoteFromList])

  // Lines are auto-filled when Reference Invoice is selected (in onValueChange)

  const formatDateForInput = (dateString?: string): string => {
    if (!dateString) return ''
    try {
      if (dateString.includes('T')) {
        return new Date(dateString).toISOString().split('T')[0]
      }
      return dateString.split('T')[0]
    } catch {
      return dateString
    }
  }

  // handleCustomerChange removed - not currently used

  // Helper function to auto-fill credit note lines from invoice lines (Full Refund - all fields)
  const autoFillLinesFromInvoice = (invLines: any[]): CreditNoteLine[] => {
    return invLines.map((invLine: any) => {
      const taxRate = taxRates.find((t) => t.taxRateId === invLine.taxRateId)
      
      const qty = invLine.qty || 0
      const unitPrice = invLine.unitPrice || 0
      const taxPercent = taxRate?.taxPercent || taxRate?.taxRate || invLine.taxRate || invLine.taxPercent || 0
      
      const itemPrice = invLine.itemPrice || (qty * unitPrice)
      const taxableAmount = itemPrice
      const taxAmount = invLine.taxAmount || (taxableAmount * (taxPercent / 100))
      const lineAmount = invLine.lineAmount || (taxableAmount + taxAmount)

      return {
        lineId: 0,
        invLineId: invLine.lineId || 0,
        creditNoteId: 0,
        itemId: invLine.itemId || 0,
        description: invLine.description || '',
        qty: qty,
        unitPrice: unitPrice,
        itemPrice: itemPrice,
        discountPercent: 0,
        discountAmount: 0,
        taxableAmount: taxableAmount,
        taxRateId: invLine.taxRateId || taxRate?.taxRateId || 0,
        taxRate: taxPercent,
        taxAmount: taxAmount,
        lineAmount: lineAmount,
        companyId: companyId,
        branchId: branchId || 0,
      }
    })
  }

  const calculateLineTotals = (line: CreditNoteLine): CreditNoteLine => {
    const qty = line.qty || 0
    const unitPrice = line.unitPrice || 0
    const taxPercent = line.taxPercent || line.taxRate || 0

    const itemPrice = qty * unitPrice
    const taxableAmount = itemPrice
    const taxAmount = taxableAmount * (taxPercent / 100)
    const lineAmount = taxableAmount + taxAmount

    return {
      ...line,
      itemPrice,
      discountPercent: 0,
      discountAmount: 0,
      taxableAmount,
      taxAmount,
      lineAmount,
    }
  }

  const summaryTotals = useMemo(() => {
    const validLines = creditNote.creditNoteLines?.filter((_, index) => !deletedRowIndices.has(index)) || []
    const subTotal = validLines.reduce((sum, line) => sum + (line.itemPrice || 0), 0)
    const totalDiscount = validLines.reduce((sum, line) => sum + (line.discountAmount || 0), 0)
    const taxableAmount = validLines.reduce((sum, line) => sum + (line.taxableAmount || 0), 0)
    const totalTax = validLines.reduce((sum, line) => sum + (line.taxAmount || 0), 0)
    const total = validLines.reduce((sum, line) => sum + (line.lineAmount || 0), 0)

    return {
      subTotal,
      totalDiscount,
      taxableAmount,
      totalTax,
      total,
    }
  }, [creditNote.creditNoteLines, deletedRowIndices])

  useEffect(() => {
    setCreditNote((prev) => ({
      ...prev,
      subTotal: summaryTotals.subTotal,
      totalDiscount: summaryTotals.totalDiscount,
      totalTax: summaryTotals.totalTax,
      total: summaryTotals.total,
    }))
  }, [summaryTotals])

  const isFormValid = useMemo(() => {
    if (!creditNote.customerId) return false
    if (!creditNote.creditNoteLines || creditNote.creditNoteLines.length === 0) return false

    const validLines = creditNote.creditNoteLines.filter((line, index) => {
      const isDeleted = deletedRowIndices.has(index)
      if (isDeleted) return false
      if (!line.description || line.description.trim() === '') return false
      if (!line.qty || line.qty <= 0) return false
      if (!line.taxRateId || line.taxRateId <= 0) return false
      return true
    })

    return validLines.length > 0
  }, [creditNote.customerId, creditNote.creditNoteLines, deletedRowIndices])

  const handleLineChange = (index: number, field: keyof CreditNoteLine, value: any) => {
    setCreditNote((prev) => {
      const newLines = [...(prev.creditNoteLines || [])]
      const updatedLine = { ...newLines[index], [field]: value }

      // Validation for QTY and UNIT PRICE - cannot exceed original invoice line values
      if (field === 'qty' && updatedLine.invLineId && updatedLine.invLineId > 0) {
        const originalLine = originalInvoiceLines.get(updatedLine.invLineId)
        if (originalLine && Number(value) > originalLine.qty) {
          showValidationError(`Quantity cannot exceed original invoice quantity: ${originalLine.qty}`)
          return prev // Don't update if validation fails
        }
      }

      if (field === 'unitPrice' && updatedLine.invLineId && updatedLine.invLineId > 0) {
        const originalLine = originalInvoiceLines.get(updatedLine.invLineId)
        if (originalLine && Number(value) > originalLine.unitPrice) {
          showValidationError(`Unit price cannot exceed original invoice unit price: ${originalLine.unitPrice.toFixed(2)}`)
          return prev // Don't update if validation fails
        }
      }

      if (field === 'taxRateId' && value) {
        const taxRate = taxRates.find((t) => t.taxRateId === Number(value))
        if (taxRate) {
          const rateValue = taxRate.taxPercent || taxRate.taxRate || 0
          updatedLine.taxRate = rateValue
          updatedLine.taxPercent = rateValue
        }
      }

      newLines[index] = calculateLineTotals(updatedLine)
      
      const remainingAmount = prev.remainingAmount ?? 0
      if (remainingAmount > 0) {
        const totalLinesAmount = newLines
          .filter((_, idx) => !deletedRowIndices.has(idx))
          .reduce((sum, line) => sum + (line.lineAmount || 0), 0)
        if (totalLinesAmount > remainingAmount) {
          showValidationError(t('creditNotes.amountCannotExceed', { total: totalLinesAmount.toFixed(2), remaining: remainingAmount.toFixed(2) }))
        }
      }
      
      return { ...prev, creditNoteLines: newLines }
    })
  }


  const handleDeleteRow = (index: number) => {
    const line = creditNote.creditNoteLines?.[index]
    if (line?.lineId && line.lineId > 0) {
      setDeletedRowIndices((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(index)) {
          newSet.delete(index)
        } else {
          newSet.add(index)
        }
        return newSet
      })
    } else {
      setCreditNote((prev) => {
        const newLines = [...(prev.creditNoteLines || [])]
        newLines.splice(index, 1)
        return { ...prev, creditNoteLines: newLines }
      })
    }
  }

  // Get user IP address
  const getUserIPAddress = async (): Promise<string> => {
    try {
      // Try to get IP from a service
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  const handleSave = async () => {
    if (!branchId) {
      await showValidationError(t('creditNotes.pleaseSelectBranchFirst'))
      return
    }

    if (!isFormValid) {
      await showValidationError(t('creditNotes.pleaseFillAllRequiredFields'))
      return
    }

    const remainingAmount = creditNote.remainingAmount ?? 0
    if (remainingAmount > 0) {
      const totalLinesAmount = creditNote.creditNoteLines
        ?.filter((_, index) => !deletedRowIndices.has(index))
        .reduce((sum, line) => sum + (line.lineAmount || 0), 0) || 0
      if (totalLinesAmount > remainingAmount) {
        await showValidationError(t('creditNotes.amountCannotExceed', { total: totalLinesAmount.toFixed(2), remaining: remainingAmount.toFixed(2) }))
        return
      }
    }

    try {
      setSaving(true)
      
      // Show loading
      showLoading(
        isEditMode ? 'Updating Credit Note...' : 'Saving Credit Note...',
        'Please wait while we process your request'
      )
      
      // Get user IP address (logged-in user's IP, not server's IP)
      const ipAddress = await getUserIPAddress()
      
      // Filter out empty lines (only save lines with actual data)
      // Deleted lines should be included with isDeleted: 'true'
      const crInvoiceLine = creditNote.creditNoteLines
        ?.map((line, originalIndex) => {
          const isDeleted = deletedRowIndices.has(originalIndex)
          if (
            !isDeleted &&
            (!line.description?.trim() || !line.qty || line.qty <= 0 || !line.taxRateId || line.taxRateId <= 0)
          ) {
            return null
          }
          
          const finalLine = calculateLineTotals(line)
          return {
            lineId: finalLine.lineId || 0,
            invLineId: finalLine.invLineId ?? 0,
            creditNoteId: creditNote.creditNoteId || 0,
            itemId: finalLine.itemId || 0,
            description: finalLine.description || '',
            qty: finalLine.qty || 0,
            unitPrice: finalLine.unitPrice || 0,
            itemPrice: finalLine.itemPrice || 0,
            discountPercent: finalLine.discountPercent || 0,
            discountAmount: finalLine.discountAmount || 0,
            taxRateId: finalLine.taxRateId || 0,
            taxAmount: finalLine.taxAmount || 0,
            lineAmount: finalLine.lineAmount || 0,
            isDeleted: isDeleted ? 'true' : 'false', // Lowercase "true" or "false" (small letters)
          }
        })
        .filter((line): line is NonNullable<typeof line> => line !== null) || []

      // Transform to API structure
      const creditNoteToSave = {
        creditNoteId: creditNote.creditNoteId || 0,
        creditNoteDate: creditNote.creditNoteDate ? new Date(creditNote.creditNoteDate).toISOString() : new Date().toISOString(),
        invoiceId: creditNote.refInvId || 0,
        subTotal: creditNote.subTotal || 0,
        totalTax: creditNote.totalTax || 0,
        totalDiscount: creditNote.totalDiscount || 0,
        total: creditNote.total || 0,
        isFullyRefund: 'false',
        note: creditNote.note || '',
        ipAddress: ipAddress,
        crInvoiceLine: crInvoiceLine,
      }

      // Log the data structure being sent
      console.log('=== Credit Note Save Data Structure ===')
      console.log('Mode:', isEditMode ? 'UPDATE' : 'CREATE')
      console.log('API Payload:', JSON.stringify(creditNoteToSave, null, 2))
      console.log('======================================')

      if (isEditMode) {
        await creditNotesService.updateCreditNote(creditNoteToSave)
      } else {
        await creditNotesService.insertCreditNote(creditNoteToSave)
      }

      // Close loading
      closeSwal()
      
      // Show success
      await showSaveSuccess(
        isEditMode,
        isEditMode ? t('creditNotes.creditNoteUpdatedSuccess') : t('creditNotes.creditNoteCreatedSuccess')
      )
      
      navigate('/credit-notes')
    } catch (error: any) {
      console.error('Failed to save credit note', error)
      console.error('Error response:', error.response?.data)
      
      // Close loading if still open
      closeSwal()
      
      // Show error
      await showSaveError(error, isEditMode)
    } finally {
      setSaving(false)
    }
  }

  const customerOptions: SearchableSelectOption[] = useMemo(() => {
    const options = customers.map((customer) => ({
      value: String(customer.customerId),
      label: customer.customerNameEn || customer.customerNameAr || `Customer ${customer.customerId}`,
    }))
    return options
  }, [customers])

  const taxRateOptions: SearchableSelectOption[] = useMemo(() => {
    const options = taxRates.map((tax) => ({
      value: String(tax.taxRateId),
      label: `${tax.taxNameEn || tax.taxNameAr || 'Tax'} (${tax.taxPercent || tax.taxRate || 0}%)`,
    }))
    console.log('Tax rate options:', options.length)
    return options
  }, [taxRates])

  // Load invoices when customer is selected
  useEffect(() => {
    if (!branchId || !selectedCustomerId || selectedCustomerId === 0) {
      setInvoices([])
      setInvoicesWithValidLines(new Set())
      return
    }

    const loadInvoices = async () => {
      try {
        console.log('Loading invoices for customer:', selectedCustomerId, 'branchId:', branchId)
        const invoicesData = await creditNotesService.getAllInvoices(branchId, selectedCustomerId)
        console.log('Loaded invoices for customer:', invoicesData.length)
        setInvoices(invoicesData || [])
        
        // Check which invoices have valid lines (at least one line with qty>0 and amount>0)
        if (invoicesData && invoicesData.length > 0) {
          setCheckingInvoiceLines(true)
          const validInvoiceIds = new Set<number>()
          
          // Check each invoice's lines
          const checkPromises = invoicesData.map(async (inv: any) => {
            const invId = inv.invoiceId || inv.id
            if (!invId) return false
            
            try {
              const response = await creditNotesService.getInvLines(invId, branchId)
              const lines = response.invoiceLines || []
              
              // Check if at least one line has qty>0 and amount>0
              const hasValidLine = lines.some((line: any) => {
                const qty = line.qty || 0
                const lineAmount = line.lineAmount || 0
                return qty > 0 && lineAmount > 0
              })
              
              if (hasValidLine) {
                validInvoiceIds.add(invId)
              }
              return hasValidLine
            } catch (error) {
              console.warn(`Failed to check lines for invoice ${invId}:`, error)
              // If we can't check, include it to be safe (don't filter out)
              validInvoiceIds.add(invId)
              return true
            }
          })
          
          await Promise.all(checkPromises)
          setInvoicesWithValidLines(validInvoiceIds)
          setCheckingInvoiceLines(false)
          console.log('Invoices with valid lines:', validInvoiceIds.size, 'out of', invoicesData.length)
        }
      } catch (error: any) {
        console.error('Failed to load invoices for customer', error)
        setInvoices([])
        setInvoicesWithValidLines(new Set())
        setCheckingInvoiceLines(false)
      }
    }

    loadInvoices()
  }, [branchId, selectedCustomerId])

  // Filter invoices: exclude fully refunded invoices, invoices with remainingAmount = 0, and invoices without valid lines
  const availableInvoices = useMemo(() => {
    console.log('Filtering invoices:', {
      totalInvoices: invoices.length,
      fullyRefundedCount: fullyRefundedInvoices.size,
      invoicesWithValidLinesCount: invoicesWithValidLines.size,
      isEditMode,
      selectedCustomerId,
    })
    
    if (isEditMode) {
      return invoices // Show all invoices in edit mode
    }
    // Filter out fully refunded invoices, invoices with remainingAmount = 0, and invoices without valid lines
    const filtered = invoices.filter((inv) => {
      const invId = inv.invoiceId || inv.id
      const isFullyRefunded = fullyRefundedInvoices.has(invId)
      const remainingAmount = inv.remainingAmount || 0
      const hasValidLines = invoicesWithValidLines.has(invId)
      
      if (isFullyRefunded) {
        console.log('Filtering out fully refunded invoice:', invId)
        return false
      }
      if (remainingAmount === 0) {
        console.log('Filtering out invoice with zero remaining amount:', invId)
        return false
      }
      // Only filter by valid lines if we've finished checking (to avoid filtering while still checking)
      if (!checkingInvoiceLines && !hasValidLines) {
        console.log('Filtering out invoice without valid lines (all lines have qty=0 or amount=0):', invId)
        return false
      }
      return true
    })
    
    console.log('Available invoices after filtering:', filtered.length)
    return filtered
  }, [invoices, fullyRefundedInvoices, invoicesWithValidLines, checkingInvoiceLines, isEditMode, selectedCustomerId])

  const invoiceOptions: SearchableSelectOption[] = useMemo(() => {
    const options = [
      { value: '0', label: 'None' },
      ...availableInvoices.map((inv) => ({
        value: String(inv.invoiceId || inv.id || 0),
        label: inv.invoiceNumber || inv.invoiceNo || `Invoice ${inv.invoiceId || inv.id || 0}`,
      })),
    ]
    console.log('Invoice options:', options.length, 'available invoices:', availableInvoices.length)
    return options
  }, [availableInvoices])


  if (loading && !creditNote.creditNoteLines?.length) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title="Sales Credit Notes"
        description={isEditMode ? t('creditNotes.updateCreditNoteDetails') : t('creditNotes.createNewCreditNote')}
      />

      {/* Client Details card - completely removed as customer name is shown in Credit Note section */}

      <Card>
        <CardHeader>
          <CardTitle>{t('creditNotes.creditNote')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <div className="space-y-2">
              <Label>{t('creditNotes.creditNoteDate')}</Label>
              <Input
                type="date"
                value={formatDateForInput(creditNote.creditNoteDate)}
                onChange={(e) => setCreditNote((prev) => ({ ...prev, creditNoteDate: e.target.value }))}
              />
            </div>
            {isEditMode && (
              <div className="space-y-2">
                <Label>{t('creditNotes.creditNoteNumberFull')}</Label>
                <Input value={creditNote.creditNoteNumber || ''} readOnly className="bg-muted" placeholder={t('creditNotes.autoGenerated')} />
              </div>
            )}
            <div className="space-y-2">
              <Label>{t('creditNotes.customer') || t('common.customer')}</Label>
              <SearchableSelect
                options={customerOptions}
                value={selectedCustomerId ? String(selectedCustomerId) : '0'}
                onValueChange={(value) => {
                  const customerId = Number(value) || 0
                  setSelectedCustomerId(customerId)
                  setCreditNote((prev) => ({
                    ...prev,
                    customerId: customerId,
                    refInvId: 0,
                    refInvNumber: '',
                    remainingAmount: 0,
                    supplyDate: '',
                    creditNoteLines: getDefaultRows(taxRates),
                  }))
                  setInvoiceLines([])
                }}
                placeholder={t('creditNotes.selectCustomer')}
                searchPlaceholder={t('creditNotes.searchCustomers')}
                emptyMessage={t('creditNotes.noCustomersFound')}
              />
            </div>
            <div className="space-y-2">
              <Label>Reference Invoice #</Label>
              <SearchableSelect
                options={invoiceOptions}
                value={creditNote.refInvId ? String(creditNote.refInvId) : '0'}
                disabled={!selectedCustomerId || selectedCustomerId === 0}
                onValueChange={async (value) => {
                  const invId = Number(value) || 0
                  const selectedInv = availableInvoices.find(inv => (inv.invoiceId || inv.id) === invId)
                  
                  if (invId === 0) {
                    setCreditNote((prev) => ({
                      ...prev,
                      refInvId: 0,
                      refInvNumber: '',
                      remainingAmount: 0,
                      supplyDate: '',
                      creditNoteLines: getDefaultRows(taxRates),
                    }))
                    setInvoiceLines([])
                    setOriginalInvoiceLines(new Map())
                    return
                  }
                  
                  let loadedLines: any[] = []
                  let invoiceData: any = null
                  if (branchId && invId) {
                    try {
                      const response = await creditNotesService.getInvLines(invId, branchId)
                      invoiceData = response.invoice || {}
                      const allLines = response.invoiceLines || []
                      
                      // Filter out lines with qty = 0 or amount = 0
                      loadedLines = allLines.filter((line: any) => {
                        const qty = line.qty || 0
                        const lineAmount = line.lineAmount || 0
                        return qty > 0 && lineAmount > 0
                      })
                      
                      // Store original invoice line values for validation (keyed by lineId which becomes invLineId)
                      const originalValuesMap = new Map<number, { qty: number; unitPrice: number }>()
                      loadedLines.forEach((line: any) => {
                        // Use lineId as key (this will match invLineId in credit note line)
                        const lineId = line.lineId || line.invLineId || 0
                        if (lineId > 0) {
                          originalValuesMap.set(lineId, {
                            qty: line.qty || 0,
                            unitPrice: line.unitPrice || 0
                          })
                        }
                      })
                      setOriginalInvoiceLines(originalValuesMap)
                      
                      setInvoiceLines(loadedLines)
                      if (loadedLines.length === 0) {
                        await showError('No Data', 'No valid invoice lines found for this invoice (lines with qty=0 or amount=0 are excluded)')
                      }
                    } catch (error: any) {
                      console.error('Failed to load invoice lines', error)
                      await showError('Error', error.response?.data?.message || 'Failed to load invoice lines')
                      setInvoiceLines([])
                      setOriginalInvoiceLines(new Map())
                    }
                  }
                  
                  const invoiceSupplyDate = invoiceData?.supplyDate || selectedInv?.supplyDate || ''
                  const newLines =
                    loadedLines.length > 0 && taxRates.length > 0
                      ? autoFillLinesFromInvoice(loadedLines)
                      : getDefaultRows(taxRates)
                  
                  setCreditNote((prev) => ({
                    ...prev,
                    refInvId: invId,
                    refInvNumber: selectedInv?.invoiceNumber || selectedInv?.invoiceNo || '',
                    remainingAmount: selectedInv?.remainingAmount || selectedInv?.total || 0,
                    customerId: selectedInv?.customerId || prev.customerId,
                    supplyDate: invoiceSupplyDate || prev.supplyDate,
                    creditNoteLines: newLines,
                  }))
                }}
                placeholder={t('creditNotes.selectInvoice')}
                searchPlaceholder={t('creditNotes.searchInvoices')}
                emptyMessage={t('creditNotes.noInvoicesFound')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('creditNotes.supplyDate')}</Label>
              <Input
                type="date"
                value={formatDateForInput(creditNote.supplyDate)}
                onChange={(e) => setCreditNote((prev) => ({ ...prev, supplyDate: e.target.value }))}
                readOnly={(creditNote.refInvId ?? 0) > 0}
                className={(creditNote.refInvId ?? 0) > 0 ? 'bg-muted' : ''}
                title={(creditNote.refInvId ?? 0) > 0 ? 'Supply date is set from invoice' : ''}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('creditNotes.remainingAmount')}</Label>
              <Input
                type="number"
                step="0.01"
                value={creditNote.remainingAmount || ''}
                readOnly
                className="bg-muted"
                placeholder="0.00"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Items Table */}
      <Card>
        <CardHeader className="bg-primary/5">
          <CardTitle>{t('creditNotes.creditNoteLines')}</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-primary/5">
                  <th className="text-left p-3 font-semibold align-middle">DESCRIPTION <span className="text-red-500">*</span></th>
                  <th className="text-left p-3 font-semibold align-middle">QTY <span className="text-red-500">*</span></th>
                  <th className="text-left p-3 font-semibold align-middle">UNIT PRICE</th>
                  <th className="text-left p-3 font-semibold align-middle">ITEM PRICE</th>
                  <th className="text-left p-3 font-semibold align-middle">TAXABLE</th>
                  <th className="text-left p-3 font-semibold align-middle">VAT <span className="text-red-500">*</span></th>
                  <th className="text-left p-3 font-semibold align-middle">VAT AMOUNT</th>
                  <th className="text-left p-3 font-semibold align-middle">AMOUNT</th>
                  <th className="text-left p-3 font-semibold align-middle w-20"></th>
                </tr>
              </thead>
              <tbody>
                {creditNote.creditNoteLines?.map((line, index) => {
                  const isDeleted = deletedRowIndices.has(index)
                  return (
                    <tr
                      key={index}
                      className={`border-b hover:bg-gray-50/50 dark:hover:bg-gray-800/50 ${
                        isDeleted ? 'bg-red-50 dark:bg-red-900/20' : ''
                      }`}
                    >
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <Input
                            value={line.description || ''}
                            onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                            placeholder="Description"
                            disabled={saving || isDeleted}
                            className="min-w-[200px]"
                            readOnly={!!(line.invLineId && line.invLineId > 0)}
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <Input
                            type="number"
                            value={line.qty || ''}
                            onChange={(e) => handleLineChange(index, 'qty', Number(e.target.value) || 0)}
                            placeholder="0"
                            disabled={saving || isDeleted}
                            className={`w-20 ${(line.qty || 0) === 1 ? 'bg-muted' : ''}`}
                            readOnly={(line.qty || 0) === 1}
                            max={line.invLineId && line.invLineId > 0 ? originalInvoiceLines.get(line.invLineId)?.qty : undefined}
                            title={line.invLineId && line.invLineId > 0 ? `Max: ${originalInvoiceLines.get(line.invLineId)?.qty || 0}` : ''}
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <Input
                            type="number"
                            step="0.01"
                            value={line.unitPrice || ''}
                            onChange={(e) => handleLineChange(index, 'unitPrice', Number(e.target.value) || 0)}
                            placeholder="0.00"
                            disabled={saving || isDeleted}
                            className={`w-24 ${(line.qty || 0) > 1 ? 'bg-muted' : ''}`}
                            readOnly={(line.qty || 0) > 1}
                            max={line.invLineId && line.invLineId > 0 ? originalInvoiceLines.get(line.invLineId)?.unitPrice : undefined}
                            title={line.invLineId && line.invLineId > 0 ? `Max: ${originalInvoiceLines.get(line.invLineId)?.unitPrice?.toFixed(2) || '0.00'}` : ''}
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <Input type="number" step="0.01" value={line.itemPrice?.toFixed(2) || '0.00'} readOnly className="bg-muted w-24" />
                      </td>
                      <td className="p-3 align-middle">
                        <Input type="number" step="0.01" value={line.taxableAmount?.toFixed(2) || '0.00'} readOnly className="bg-muted w-24" />
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <SearchableSelect
                            options={taxRateOptions}
                            value={line.taxRateId ? String(line.taxRateId) : ''}
                            onValueChange={(value) => handleLineChange(index, 'taxRateId', Number(value))}
                            placeholder="Select VAT"
                            searchPlaceholder="Search VAT..."
                            emptyMessage="No VAT rates found"
                            disabled={saving || isDeleted}
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <Input type="number" step="0.01" value={line.taxAmount?.toFixed(2) || '0.00'} readOnly className="bg-muted w-24" />
                      </td>
                      <td className="p-3 align-middle">
                        <Input type="number" step="0.01" value={line.lineAmount?.toFixed(2) || '0.00'} readOnly className="bg-muted w-24" />
                      </td>
                      <td className="p-3 align-middle">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteRow(index)}
                          disabled={saving}
                          className={
                            isDeleted
                              ? 'text-green-600 hover:text-green-700 hover:bg-green-50'
                              : 'text-destructive hover:text-destructive hover:bg-destructive/10'
                          }
                          title={isDeleted ? 'Undo delete' : 'Delete row'}
                        >
                          {isDeleted ? <span className="text-xs font-semibold">Undo</span> : <Trash2 className="w-4 h-4" />}
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-6 bg-primary/5 p-4 rounded-md">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm text-muted-foreground">SUB TOTAL</Label>
                <p className="text-lg font-semibold">{summaryTotals.subTotal.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">TOTAL VAT</Label>
                <p className="text-lg font-semibold">{summaryTotals.totalTax.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">TOTAL</Label>
                <p className="text-lg font-semibold">{summaryTotals.total.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Notes / Remarks</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={creditNote.note || ''}
            onChange={(e) => setCreditNote((prev) => ({ ...prev, note: e.target.value }))}
            placeholder="Enter notes or remarks..."
            className="min-h-[100px]"
          />
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row justify-end gap-4">
        <Button type="button" variant="outline" onClick={() => navigate('/credit-notes')} className="w-full sm:w-auto">
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || !branchId || !isFormValid}
          className="w-full sm:w-auto min-w-[150px]"
          title={!isFormValid ? t('creditNotes.pleaseFillAllRequiredFields') : ''}
        >
          {saving ? (
            <>
              <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {t('creditNotes.saving')}
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {isEditMode ? t('creditNotes.updateCreditNote') : t('creditNotes.saveCreditNote')}
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

